import React, { useState, useCallback, useMemo, useEffect } from "react";

import {
  Box,
  Typography,
  Paper,
  Grid,
  ThemeProvider,
  Backdrop,
  CircularProgress,
} from "@mui/material";

import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import GlobalCard from "../../newComponent/NewCardsComp/GlobalCardComp";
import InputSelect from "../../newComponent/themeprovider/textField/InputSelect";
import CustomToggle from "../../newComponent/themeprovider/toggle/CustomToggle";
import DayTabs from "./component/DayTabs";
import CustomNewButton from "../../newComponent/themeprovider/button/CustomNewButton";
import LoaderComp from "../../newComponent/LoaderComp/Loader";
import mainTheme from "../../newComponent/themeprovider/mainTheme/MainTheme";
import CustomSnackbar from "../../components/ui-component/snackbar/CustomSnackbar";
import ViewMode from "./ViewMode";
import EditMode from "./EditMode";


const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT;

/* ================= DEFAULT ================= */

const emptyLesson = {
  topic: "",
  objective: "",
  content: "",
  activity: "",
  homework: "",
  remarks: "",
  
  study_material: [],
  presentation_files: [],

};

const normalizeLesson = (data) => {
  return {
    topic: data?.topic || "",
    objective: data?.objective || "",
    content: data?.content || "",
    activity: data?.activity || "",
    homework: data?.homework || "",
    remarks: data?.remarks || "",
   
    study_material: data?.study_material_obj || [],
    presentation_files: data?.presentation_file_obj || [],

  };
};

/* ================= COMPONENT ================= */

const LessonPlanner = () => {
  const userId = localStorage.getItem("userId");

  const [testType, setTestType] = useState("");
  const [testTypeList, setTestTypeList] = useState([]);

  const [duration, setDuration] = useState("");
  const [durationList, setDurationList] = useState([]);
  const [selectedDuration, setSelectedDuration] = useState(null);

  const [days, setDays] = useState([]);
  const [lessons, setLessons] = useState([]);

  const [activeDayIndex, setActiveDayIndex] = useState(0);

  const [mode, setMode] = useState("edit");
  const [isLoading, setIsLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [saveLoading, setSaveLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  /* ================= FETCH TEST TYPE ================= */

  useEffect(() => {
    const fetchTestType = async () => {
      try {
        const res = await fetch(`${API_ENDPOINT}route/getalltesttype`);

        const data = await res.json();

        if (res.ok) {
          setTestTypeList(data);
        }
      } catch (err) {
        console.error(err);
      }
    };

    fetchTestType();
  }, []);

  /* ================= FETCH DURATION ================= */

  useEffect(() => {
    if (!testType) return;

    const fetchDuration = async () => {
      try {
        const res = await fetch(
          `${API_ENDPOINT}package/by-testtype/${testType}`,
        );

        const data = await res.json();

        if (res.ok) {
          setDurationList(data);
        }
      } catch (err) {
        console.error(err);
      }
    };

    fetchDuration();
  }, [testType]);

  /* ================= CREATE PLAN ================= */
  console.log(" selectedDuration", selectedDuration);
  const handleCreatePlan = async () => {
    if (!duration || !testType) {
      showSnackbar("Please select duration and test type", "error");
      return;
    }

    setIsLoading(true);

    try {
      const res = await fetch(`${API_ENDPOINT}lesson-plan`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          duration,
          test_type_id: testType,
          package_id: selectedDuration?.id,
          user_id: Number(userId),
        }),
      });

      const data = await res.json();

      if (res.ok && data?.data) {
        setDays(data.data);

        const lessonArr = Array.from({ length: data.data.length }, () => ({
          ...emptyLesson,
        }));

        setLessons(lessonArr);
        setActiveDayIndex(0);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  /* ================= ACTIVE DAY ================= */

  const activeDay = useMemo(() => {
    return days[activeDayIndex] || "";
  }, [days, activeDayIndex]);

  /* ================= CURRENT LESSON ================= */

  const currentLesson = useMemo(() => {
    return lessons[activeDayIndex] || emptyLesson;
  }, [lessons, activeDayIndex]);

  /* ================= HANDLE CHANGE ================= */

  const handleChange = useCallback(
    (field, value) => {
      setLessons((prev) => {
        const updated = [...prev];

        updated[activeDayIndex] = {
          ...updated[activeDayIndex],
          [field]: value,
        };

        return updated;
      });
    },
    [activeDayIndex],
  );

  /* ================= SAVE DAY ================= */

  const handleSaveDay = async () => {
    if (!activeDay) return;
    if(!currentLesson?.topic){
      showSnackbar("Please enter topic", "error");
      return
    }
    if(!currentLesson?.objective){
      showSnackbar("Please enter objective", "error");
      return
    }
    if(!currentLesson?.content){
      showSnackbar("Please enter content", "error");
      return
    }

    if(!currentLesson?.activity){
      showSnackbar("Please enter activity", "error");
      return
    }


    setSaveLoading(true);

    const formData = new FormData();

    formData.append("plan_id", activeDay.plan_id);
    formData.append("day_number", activeDay.day_number);

    formData.append("topic", currentLesson.topic);
    formData.append("objective", currentLesson.objective);
    formData.append("content", currentLesson.content);
    formData.append("activity", currentLesson.activity);
    formData.append("remarks", currentLesson.remarks);

    formData.append("user_id", Number(userId));

  

    try {
      const res = await fetch(`${API_ENDPOINT}lesson-plan-day`, {
        method: "POST",
        body: formData, // send FormData directly
      });

      if (res.ok) {
        console.log("Saved successfully");
        showSnackbar("Lesson Plan Uploaded Successfully", "success");
      } else {
        throw new Error("Request failed");
      }
    } catch (err) {
      console.error(err);
      showSnackbar("Failed To Upload Lesson Plan", "error");
    } finally {
      setSaveLoading(false);
    }
  };

  /* ================= FETCH DAY DATA ================= */

  const handleFetchLesson = async () => {
    if (!activeDay) return;

    try {
      setIsLoading(true);

      const res = await fetch(`${API_ENDPOINT}lesson-plan-day/get-data`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          plan_id: activeDay.plan_id,
          day_number: activeDay.day_number,
        }),
      });

      const data = await res.json();

      if (res.ok && data?.data) {
        setLessons((prev) => {
          const updated = [...prev];

          updated[activeDayIndex] = normalizeLesson(data.data);

          return updated;
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  console.log("lessons", lessons);
  console.log("currentLesson", currentLesson);

  useEffect(() => {
    handleFetchLesson();
  }, [activeDayIndex, activeDay]);

  useEffect(() => {
    const selectedDuartion = durationList.find((d) => d.duration === duration);
    if (selectedDuartion) {
      setSelectedDuration(selectedDuartion);
    }
  }, [duration]);

  // upload file

  const handleUploadFiles = async (
    presentationFile = null,
    studyFiles = null,
    type
  ) => {
    console.log("studyFiles", presentationFile);
    if (!activeDay) return;

    const formData = new FormData();

    formData.append("plan_id", activeDay.plan_id);
    formData.append("day_number", activeDay.day_number);
    formData.append("user_id", userId);

    let hasFile = false;

    // Presentation
 if (presentationFile instanceof File) {
  formData.append("file", presentationFile);
  hasFile = true;
}


    

    // Study material
   if (studyFiles instanceof File) {
      formData.append("study_material", studyFiles);
      hasFile = true;
    }

    console.log("hasFile", hasFile);

    if (!hasFile) return;
setUploading(true);
    try {
      const res = await fetch(`${API_ENDPOINT}lesson-plan-day/upload-file`, {
        method: "POST",
        body: formData,
      });

      console.log("response", res);

      if (res.status === 200) {
        showSnackbar("File Uploaded Successfully", "success");

    console.log("presentationFile", presentationFile);
   
   // Update Presentation Files
if (type === "presentation_file") {
  handleChange("presentation_files",[
    ...(currentLesson.presentation_files || []),
    presentationFile,
  ]);
}

  // Update Study Material
if (type === "study_material") {
  handleChange("study_material", [
    ...(currentLesson.study_material || []),
    studyFiles,
  ]);
}

setUploading(false);

      
    
      } else {
        setUploading(false);
        throw new Error();
      }
    } catch (err) {
      console.error(err);
      showSnackbar("Upload Failed", "error");
      setUploading(false);
    }
  };

  console.log("currentLesson.presentation_files", currentLesson.presentation_files);

  // delete Presentation

  const handleDeleteFile = async (type, fileName) => {
    if (!activeDay || !fileName) return;

    try {
      const res = await fetch(`${API_ENDPOINT}lesson-plan-day/delete-file`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          plan_id: activeDay.plan_id,
          day_number: activeDay.day_number,
          user_id: Number(userId),

          study_material: type === "study_material" ? fileName : null,
          presentation_file: type === "presentation_file" ? fileName : null,
        }),
      });

      if (res.ok) {
        showSnackbar("Deleted Successfully", "success");

        // Update UI after delete
        setLessons((prev) => {
          const updated = [...prev];

          if (type === "presentation_file") {
  updated[activeDayIndex].presentation_files =
    updated[activeDayIndex].presentation_files.filter(
      (f) => f?.original_filename !== fileName && f?.name !== fileName
    );
}


          if (type === "study_material") {
            updated[activeDayIndex].study_material = updated[
              activeDayIndex
            ].study_material.filter(
              (f) => f?.original_filename !== fileName && f?.name !== fileName,
            );
          }

          return updated;
        });
      } else {
        throw new Error();
      }
    } catch (err) {
      console.error(err);
      showSnackbar("Delete Failed", "error");
    }
  };

  console.log("currentLesson", currentLesson);
  /* ================= UI ================= */

  return (
    <>
      <LoaderComp isLoading={isLoading} />
      <Backdrop open={saveLoading} style={{ zIndex: 9999 }}>
        <Typography variant="h4" color="error" fontWeight={800} marginRight={2}>
          Saving Data ...
        </Typography>
        <CircularProgress color="primary" />
      </Backdrop>
      <Backdrop open={uploading} style={{ zIndex: 9999 }}>
        <Typography variant="h4" color="error" fontWeight={800} marginRight={2}>
          Uploading ...
        </Typography>
        <CircularProgress color="primary" />
      </Backdrop>
      <ThemeProvider theme={mainTheme}>
        <Grid container spacing={2}>
          {/* TOP BAR */}
          <Grid item xs={12}>
            <GlobalCard>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <InputSelect
                    label="Select Test Type"
                    value={testType}
                    onChange={(e) => {setTestType(e.target.value) ; setDuration('')}}
                    options={testTypeList}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <InputSelect
                    label=" Select Duration"
                    value={duration}
                    onChange={(e) => setDuration(e.target.value)}
                    options={
                      !testType
                        ? [
                            {
                              id: "no-test",
                              duration: "",
                              label: "Please select test type first",
                              disabled: true,
                            },
                          ]
                        : testType && durationList?.length===0
                        ? [
                            {
                              id: "no-duration",
                              duration: "",
                              label: "There is no duration available for this test type.",
                              disabled: true,
                            },
                        ]:
                        durationList
                    }
                    getOptionLabel={(o) => o.label || `${o.duration} Days`}
                    getValue={(o) => o.duration}
                    getOptionDisabled={(o) => o.disabled} // 👈 important
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <CustomNewButton
                  disabled={!testType || !duration}
                    label="Create Plan"
                    onClick={handleCreatePlan}
                  />
                </Grid>
              </Grid>
            </GlobalCard>
          </Grid>

          {/* TABS */}
          {days.length > 0 && (
            <Grid item xs={12}>
              <GlobalCard>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <DayTabs
                      days={days}
                      activeDayIndex={activeDayIndex}
                      setActiveDayIndex={setActiveDayIndex}
                    />
                  </Grid>

                  {/* MODE */}
                  <Grid item xs={12}>
                    <CustomToggle
                      value={mode}
                      onChange={setMode}
                      options={[
                        {
                          label: "Edit",
                          value: "edit",
                        },
                        {
                          label: "View",
                          value: "view",
                        },
                      ]}
                    />
                  </Grid>

                  {/* EDIT MODE */}
                  {mode === "edit" && (
                    <Grid item xs={12}>
                      <EditMode
                        currentLesson={currentLesson}
                        handleChange={handleChange}
                        handleSaveDay={handleSaveDay}
                        handleDeleteFile={handleDeleteFile}
                        handleUploadFiles={handleUploadFiles}
                      />
                    </Grid>
                  )}

                  {mode === "view" && (
                    <Grid item xs={12}>
                      <ViewMode
                        currentLesson={currentLesson}
                        activeDay={activeDay}
                        activeDayIndex={activeDayIndex}
                      />
                    </Grid>
                  )}
                </Grid>
              </GlobalCard>
            </Grid>
          )}
        </Grid>
      </ThemeProvider>

      <CustomSnackbar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </>
  );
};

export default LessonPlanner;
